import { defineComponent as _defineComponent } from 'vue'
import {computed, nextTick, onMounted, reactive, ref, toRefs, watch} from "vue";
  import {isAuthorized, t} from "@/mixin/mixins";
  import EmployeeService, {EmployeeListItem, EmployeeV3Filter} from "@/service/EmployeeService";
  import {EmployeeStatus} from "@/enums";

  declare interface SortingCriterion {
    value: string,
    localizedName: string
  }

  
export default /*#__PURE__*/_defineComponent({
  __name: 'EmployeesList',
  setup(__props) {

  const searchField = ref();

  const sortCriterionName = {value: "name", localizedName: t("name")} as SortingCriterion;
  const sortCriterionEmployeeId = {value: "employeeId", localizedName: t("employee.employeeId")} as SortingCriterion;
  const sortCriterionWage = {value: "wage", localizedName: t("accounting.wageOrSalary")} as SortingCriterion;

  // The v3 API forbids sorting by wage for callers who may not see wages, and leaves the wage out for them
  const canViewWages = isAuthorized("Accounting_Overview_ViewWageAndCosts");

  const initialState = {
    activeSort: {
      criterion: sortCriterionName,
      direction: "ASC"
    },
    currentPage: 0, // used to track the current page in case of scrolling
    totalPages: 0, // used to stop loading further pages once the last one is shown
    employees: new Array<EmployeeListItem>(),
    filter: "",
    loading: false,
    searchFieldVisible: false,
    showBottomSheet: false,
    showInactiveEmployees: false,
    sortingCriteria: [
      sortCriterionName,
      sortCriterionEmployeeId,
      ...(canViewWages ? [sortCriterionWage] : [])
    ]
  };

  const state = reactive({...initialState});

  const sortedEmployees = computed(() => {
    return state.employees;
  });

  onMounted(() => {
    load();
  });

  watch(() => state.showInactiveEmployees, () => {
    load();
  });

  watch(() => state.filter, () => {
    load();
  });

  function load(page = 0) {
    if (state.loading) {
      return;
    }

    state.loading = true;

    let filter: EmployeeV3Filter = {
      term: state.filter,
      // If we also display inactive employees we do not need this filter
      status: state.showInactiveEmployees ? null : EmployeeStatus.ACTIVE
    };

    let activeSortCriterion = state.activeSort.criterion.value;
    if (activeSortCriterion === "name") {
      activeSortCriterion = "lastName";
    } else if (activeSortCriterion === "employeeId") {
      activeSortCriterion = "employeeNumber";
    }
    let sort = state.activeSort.direction === "ASC" ? activeSortCriterion : activeSortCriterion + ",desc";

    EmployeeService.findEmployeeListItemsV3(filter, 25, page, sort)
      .then(result => {
        let employees = result.content;
        // In case the page is not 0 we load data via scrolling, so we want to append the entries
        if (page > 0) {
          state.employees.push(...employees);
        } else {
          state.employees = employees;
        }
        state.currentPage = result.page.number;
        state.totalPages = result.page.totalPages;
      })
      .finally(() => state.loading = false);
  }

  function changeSort(sortingCriterion: SortingCriterion) {
    state.showBottomSheet = false;

    let direction = "ASC";
    if (state.activeSort.criterion === sortingCriterion) {
      direction = (state.activeSort.direction === "ASC" ? "DESC" : "ASC");
    }

    state.activeSort = {
      criterion: sortingCriterion,
      direction: direction
    };

    load();
  }

  function onScroll(event: any) {
    const maxHeight = document.body.scrollHeight;
    const scrollPosition = event.target.documentElement.scrollTop;
    const windowHeight = window.innerHeight;

    if ((scrollPosition + windowHeight) >= maxHeight && state.currentPage + 1 < state.totalPages) {
      load(state.currentPage + 1);
    }
  }

  function showSearchField() {
    state.searchFieldVisible = true;
    nextTick(() => {
      searchField.value.focus();
    });
  }

  function clearSearch() {
    state.filter = "";
    nextTick(() => {
      state.searchFieldVisible = false;
    });
  }

  const {activeSort, sortingCriteria} = toRefs(state);

return { __sfc: true,searchField, sortCriterionName, sortCriterionEmployeeId, sortCriterionWage, canViewWages, initialState, state, sortedEmployees, load, changeSort, onScroll, showSearchField, clearSearch, activeSort, sortingCriteria, t }
}

})